import React, { useState } from 'react';
import Nav from '../../components/Nav';
import Footer from '../../components/Footer';
import { submitInstructorApplication } from '../../services/api';
import { Award, DollarSign, Users, BookOpen, CheckCircle, Send, Loader2 } from 'lucide-react';

const TeachOnELearn = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', topic: '', experience: '', bio: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await submitInstructorApplication(formData);
      if (res.success) {
        setSubmitted(true);
      } else {
        setError(res.message || 'Failed to submit application. Please try again.');
      }
    } catch (err) {
      console.error("Error submitting application:", err);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-14 sm:pt-16">
      <Nav />
      <main className="flex-1 max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-5 w-full space-y-3.5 sm:space-y-5">
        
        {/* Compact Hero */}
        <div className="bg-gradient-to-r from-emerald-800 via-teal-900 to-emerald-900 rounded-xl sm:rounded-2xl p-3 sm:py-3.5 sm:px-5 md:py-4 md:px-6 text-white shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <div className="space-y-1 max-w-xl">
            <span className="bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
              Instructor Network
            </span>
            <h1 className="text-sm sm:text-lg md:text-xl font-black text-white leading-snug">
              Teach with us & empower millions of learners
            </h1>
            <p className="text-emerald-100/90 text-[11px] sm:text-xs leading-snug">
              Become an instructor on E-Learning Hub. Create online courses, reach students globally, and earn revenue sharing on every enrollment.
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 p-2 sm:px-3.5 sm:py-2 rounded-xl text-center shrink-0 w-full sm:w-auto">
            <h3 className="text-lg sm:text-xl font-black text-white">75%</h3>
            <p className="text-[8px] sm:text-[9px] text-emerald-200 uppercase tracking-wider font-bold">Instructor Revenue Share</p>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-2xs space-y-1.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <DollarSign size={16} />
            </div>
            <h3 className="text-xs sm:text-sm font-bold text-gray-900">Earn Income</h3>
            <p className="text-[11px] sm:text-xs text-gray-500 leading-normal">Earn money every time a student purchases your course with monthly payouts.</p>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-2xs space-y-1.5">
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
              <Users size={16} />
            </div>
            <h3 className="text-xs sm:text-sm font-bold text-gray-900">Inspire Learners</h3>
            <p className="text-[11px] sm:text-xs text-gray-500 leading-normal">Teach what you know and help students master industry-ready technical skills.</p>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-2xs space-y-1.5">
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center font-bold">
              <Award size={16} />
            </div>
            <h3 className="text-xs sm:text-sm font-bold text-gray-900">Dedicated Support</h3>
            <p className="text-[11px] sm:text-xs text-gray-500 leading-normal">Get course creation guides, video tools, and marketing assistance from our team.</p>
          </div>
        </div>

        {/* Application Form */}
        <div className="bg-white p-3.5 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-100 shadow-2xs max-w-2xl mx-auto">
          <div className="text-center mb-3 sm:mb-4">
            <h2 className="text-sm sm:text-lg font-bold text-gray-900">Apply to Become an Instructor</h2>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">Fill out the form below and our team will review your application within 24 hours.</p>
          </div>

          {submitted ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-xl text-center space-y-1.5">
              <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto" />
              <h3 className="text-sm font-bold">Application Submitted!</h3>
              <p className="text-xs">Thank you, {formData.name}. We will contact you at {formData.email} / {formData.phone} shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-2.5 sm:space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Full Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Email Address</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                    placeholder="name@example.com"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Phone Number</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                    placeholder="e.g. 7605812086"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Course Topic / Field</label>
                  <input
                    type="text"
                    required
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                    placeholder="e.g. Electrical Vehicle, Python"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Teaching Experience</label>
                  <select
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                  >
                    <option value="">Select experience level</option>
                    <option value="Beginner (0-2 years)">Beginner (0-2 years)</option>
                    <option value="Intermediate (2-5 years)">Intermediate (2-5 years)</option>
                    <option value="Expert (5+ years)">Expert (5+ years)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Bio & Course Proposal</label>
                <textarea
                  rows={2}
                  required
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs"
                  placeholder="Tell us about yourself and the course you want to teach..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-emerald-600 text-white font-bold py-2 sm:py-2.5 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5 text-xs sm:text-sm shadow-sm cursor-pointer"
              >
                <Send size={15} /> Submit Application
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TeachOnELearn;
